import { Trans, useTranslation } from 'react-i18next'
import {
    ModelsRefundPaymentMethod,
    PkgInfrastructureRestRefundMethod
} from '@itsrever/returns-api-types'
import { useTranslate } from '@/hooks'
import { useAppSelector } from '@/redux/hooks'
import { RefundTimings } from '@/redux/return/returnSlice'
import { FlashIcon } from '@/ui/icons/flash'
import { formatStringPrice } from '@/utils'
import { TimerIcon } from '@/ui/icons/timer'

interface RefundMethodCardProps {
    method: PkgInfrastructureRestRefundMethod
    isSelected: boolean
    isGiftCardOffered: boolean
}

export const RefundMethodCard = ({
    method: { method, timing, shipping_costs, service_fees, rdv_surplus },
    isSelected,
    isGiftCardOffered
}: RefundMethodCardProps) => {
    const {
        i18n: { language },
        t
    } = useTranslation()
    const { translate } = useTranslate()

    const currency = useAppSelector(
        (store) => store.returnsApi.order.response?.order?.money_format
    )?.currency

    const provisionalRefundAmount = useAppSelector(
        (store) => store.refundMethodsApi.provisionalRefundAmount
    )

    const calculateServiceFeesAmount = () => {
        if (service_fees?.service_fees_amount) {
            return service_fees.service_fees_amount / 100
        }

        return service_fees?.service_fees_percentage
            ? ((provisionalRefundAmount ?? 0) / 100) *
                  (service_fees.service_fees_percentage / 100)
            : 0
    }

    const calculateRDVSurplusAmount = () => {
        if (rdv_surplus?.surplus_amount) {
            return rdv_surplus?.surplus_amount / 100
        }

        return rdv_surplus?.surplus_percentage
            ? ((provisionalRefundAmount ?? 0) / 100) *
                  (rdv_surplus.surplus_percentage / 100)
            : 0
    }

    const isGiftCard =
        method === ModelsRefundPaymentMethod.RefundPaymentMethod_GIFT_CARD ||
        method === ModelsRefundPaymentMethod.RefundPaymentMethod_PROMO_CODE

    const minimumShippingCost = parseFloat(shipping_costs?.min?.amount || '0')
    const maximumShippingCost = parseFloat(shipping_costs?.max?.amount || '0')

    const shouldShowServiceFees =
        !service_fees?.apply_only_if_payment_at_checkout &&
        (service_fees?.service_fees_amount ||
            service_fees?.service_fees_percentage)

    const shouldShowRDVSurplus =
        (rdv_surplus?.surplus_amount ?? 0) > 0 ||
        (rdv_surplus?.surplus_percentage ?? 0) > 0

    const shouldShowFreeShipping = isGiftCard && minimumShippingCost === 0

    const shouldShowRefundTiming = timing === RefundTimings.OnStartReturnProcess

    const isSingleShippingCost = minimumShippingCost === maximumShippingCost

    const totalRefundAmount =
        (provisionalRefundAmount ?? 0) / 100 -
        (isGiftCard ? minimumShippingCost : maximumShippingCost) -
        (shouldShowServiceFees ? calculateServiceFeesAmount() : 0) +
        (shouldShowRDVSurplus ? calculateRDVSurplusAmount() : 0)

    return (
        <div
            data-selected={isSelected}
            data-giftcard={isGiftCard}
            data-freeshipping={shouldShowFreeShipping}
            className="relative cursor-pointer rounded-md border-2 border-solid border-neutral-200 bg-white p-5 shadow-sm transition-all hover:shadow-sm data-[selected=true]:border-neutral-400/70 data-[selected=true]:data-[giftcard=true]:border-blue-400 data-[selected=true]:bg-neutral-100/60 data-[selected=true]:data-[giftcard=true]:bg-blue-50/50 data-[freeshipping=true]:data-[giftcard=true]:pt-6 data-[selected=true]:data-[giftcard=true]:shadow-[0px_0px_6px_0px_rgba(59,_130,_246,_0.5)]"
        >
            {shouldShowFreeShipping ? (
                <div className="absolute left-4 top-0 -translate-y-1/2 rounded-full bg-[radial-gradient(160.21%_97.87%_at_50%_100%,_#17B9FF_0%,_#1774FF_100%),_linear-gradient(96deg,_#17B9FF_25.03%,_#1774FF_49.49%,_#1B3FFF_75.47%),_#1790FF] bg-blue-500 px-5 py-1 shadow-[inset_0_0_10px_#FFF,_0_3px_3px_-3px_rgba(5,150,105,0.32),_0_4px_4px_-2px_rgba(5,150,105,0.2)]">
                    <p
                        className="text-sm font-medium uppercase tracking-wide text-white"
                        dangerouslySetInnerHTML={{
                            __html: translate('refund_card_free_shipping')
                        }}
                    />
                </div>
            ) : null}
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <h2
                        dangerouslySetInnerHTML={{
                            __html: translate(
                                `refund_card_method${method}_title`
                            )
                        }}
                        className="font-[Roobert] text-base font-medium capitalize text-neutral-800"
                    />
                </div>

                <p className="flex items-center gap-2 text-xs font-normal text-neutral-600">
                    {t('refund_card.shipping_costs')}{' '}
                    {shouldShowFreeShipping ? (
                        formatStringPrice(
                            '0',
                            shipping_costs?.min?.currency,
                            language
                        )
                    ) : isSingleShippingCost ? (
                        formatStringPrice(
                            shipping_costs?.min?.amount,
                            shipping_costs?.min?.currency,
                            language
                        )
                    ) : isGiftCardOffered ? (
                        <>
                            {t('refund_card.up_to')}{' '}
                            {formatStringPrice(
                                shipping_costs?.max?.amount,
                                shipping_costs?.max?.currency,
                                language
                            )}
                        </>
                    ) : (
                        <>
                            {t('refund_card.from')}{' '}
                            {formatStringPrice(
                                shipping_costs?.min?.amount,
                                shipping_costs?.min?.currency,
                                language
                            )}{' '}
                            - {t('refund_card.up_to')}{' '}
                            {formatStringPrice(
                                shipping_costs?.max?.amount,
                                shipping_costs?.max?.currency,
                                language
                            )}
                        </>
                    )}
                </p>
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-12">
                    <div className="space-y-2">
                        <p
                            data-selected={isSelected}
                            className="text-sm font-normal leading-normal text-neutral-500 data-[selected=true]:text-neutral-800"
                            dangerouslySetInnerHTML={{
                                __html: translate(
                                    `refund_card_method${method}_description${timing}`
                                )
                            }}
                        />
                        {shouldShowRDVSurplus ? (
                            <p>
                                <span className="text-sm font-medium text-[#1790FF]">
                                    <Trans
                                        i18nKey="refund_card.rdv_surplus"
                                        values={{
                                            rdv_surplus_amount:
                                                formatStringPrice(
                                                    calculateRDVSurplusAmount().toString(),
                                                    currency,
                                                    language
                                                )
                                        }}
                                    />
                                </span>
                            </p>
                        ) : null}
                        {shouldShowServiceFees ? (
                            <p className="flex items-center gap-1.5 text-xs font-normal text-neutral-600">
                                {t('service_fees.tooltip_message', {
                                    fee: formatStringPrice(
                                        calculateServiceFeesAmount().toString(),
                                        currency,
                                        language
                                    )
                                })}
                            </p>
                        ) : null}
                    </div>
                    <div className="-mt-1 flex shrink-0 flex-col items-end gap-1">
                        <p
                            data-giftcard={isGiftCard}
                            className="flex items-baseline gap-0.5 text-xl font-semibold data-[giftcard=true]:text-[#1790FF] md:text-2xl"
                        >
                            {formatStringPrice(
                                totalRefundAmount.toString(),
                                currency,
                                language
                            )}
                        </p>
                        {shouldShowRefundTiming ? (
                            <p
                                data-selected={isSelected}
                                className="flex items-center gap-1 text-xs font-medium text-neutral-600 data-[selected=true]:text-neutral-800"
                            >
                                {isGiftCard ? (
                                    <FlashIcon height={14} width={14} />
                                ) : (
                                    <TimerIcon height={14} width={14} />
                                )}
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: translate(
                                            `refund_card_time_method${method}`
                                        )
                                    }}
                                />
                            </p>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    )
}
